import { ChordAnalytics, type ObjectTypes } from '@chordcommerce/analytics'

import { updateCartAttributesReconcile } from '@/clients/shopify/client'
import {
  cartFormatter,
  checkoutFormatter,
  lineItemFormatter,
  productFormatter,
} from '@/config/chord-analytics-formatters'
import type { Cart } from '@/types/shopify'
import { captureException } from '@/utilities/logger-fe'
import { withTimeout } from '@/utilities/timing'

export const TRACKING_TIMEOUT = 300

const {
  PUBLIC_CHORD_CDP_DOMAIN,
  PUBLIC_CHORD_CDP_WRITE_KEY,
  PUBLIC_OMS_ID,
  PUBLIC_STORE_ID,
  PUBLIC_TENANT_ID,
  PUBLIC_SHOPIFY_STORE_NAME,
} = import.meta.env

const hasRequiredEnvironmentVariables =
  PUBLIC_CHORD_CDP_DOMAIN &&
  PUBLIC_CHORD_CDP_WRITE_KEY &&
  PUBLIC_OMS_ID &&
  PUBLIC_STORE_ID &&
  PUBLIC_TENANT_ID &&
  PUBLIC_SHOPIFY_STORE_NAME

export const chord =
  typeof window !== 'undefined' && hasRequiredEnvironmentVariables
    ? new ChordAnalytics<ObjectTypes>({
        cdpDomain: PUBLIC_CHORD_CDP_DOMAIN,
        cdpWriteKey: PUBLIC_CHORD_CDP_WRITE_KEY,
        consent: 'ketch',
        awaitConsent: false,
        formatters: {
          objects: {
            cart: cartFormatter,
            checkout: checkoutFormatter,
            lineItem: lineItemFormatter,
            product: productFormatter,
          },
        },
        metadata: {
          i18n: {
            currency: 'USD',
            locale: 'en-us',
          },
          ownership: {
            omsId: PUBLIC_OMS_ID,
            storeId: PUBLIC_STORE_ID,
            tenantId: PUBLIC_TENANT_ID,
          },
          platform: {
            name: 'Shopify',
            type: 'web',
          },
          store: {
            domain: PUBLIC_SHOPIFY_STORE_NAME,
          },
        },
      })
    : null

export const saveChordUserIdToCart = async (cart: Cart) => {
  if (window._chord) {
    try {
      const analyticsUserId = window._chord.user().anonymousId
      const isChordIdPresent = (cart.attributes || []).some(
        (attribute) => attribute.key === '_cdpAnonymousId' && !!attribute.value,
      )

      if (!analyticsUserId || isChordIdPresent) {
        console.warn(
          'Chord CDP user is either not available or was already added to cart attributes',
        )
      } else {
        await updateCartAttributesReconcile(cart.id, [
          ...cart.attributes,
          { key: '_cdpAnonymousId', value: analyticsUserId },
        ])
      }
    } catch (error) {
      captureException(error)
    }
  }
}

export const withChordTimeout = async <T>(
  promise: Promise<T>,
): Promise<T | undefined> => {
  try {
    return await withTimeout(promise, TRACKING_TIMEOUT)
  } catch (error) {
    return undefined
  }
}
